// SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

import { html, LitElement, nothing } from 'lit';
import { property } from 'lit/decorators/property.js';
import { FormControlMixin } from '@nvidia-elements/forms/mixins';
import type { KeynavListConfig } from '@nvidia-elements/core/internal';
import {
  attachInternals,
  I18nController,
  formatStandardNumber,
  keyNavigationList,
  typeSSR,
  useStyles,
  scopedRegistry
} from '@nvidia-elements/core/internal';
import { IconButton } from '@nvidia-elements/core/icon-button';
import { Select } from '@nvidia-elements/core/select';
import styles from './pagination.css?inline';

/* eslint-disable jsdoc/no-types */
// explicit jsdoc annotation due to inheritance context lost with CEM generator

/**
 * @element nve-pagination
 * @description Pagination is a control that enables users to navigate through pages of content.
 * @documentation https://nvidia.github.io/elements/docs/elements/pagination/
 * @since 0.11.0
 * @entrypoint \@nvidia-elements/core/pagination
 * @event input - Dispatched when the value (page) has changed
 * @event change - Dispatched when the value (page) has changed
 * @event first-page - Dispatched when the first page is active
 * @event last-page - Dispatched when the last page is active
 * @event step-change - Dispatched after the page size changes with the current page size in `detail`. The event bubbles and crosses shadow boundaries.
 * @slot suffix-label - Overrides the "of total" label when the total is an approximation.
 * @cssprop --background
 * @cssprop --font-size
 * @cssprop --width
 * @csspart icon-button - Base part applied to all icon button elements
 * @csspart previous-icon-button - The previous page icon button
 * @csspart next-icon-button - The next page icon button
 * @csspart start-icon-button - The first page icon button
 * @csspart end-icon-button - The last page icon button
 * @csspart select - The page size select element
 * @aria https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/
 * @property {number} value - value the current page number
 * @attribute {string} step-sizes - A JSON-serialized array of page-size options available in the selector.
 */
@typeSSR()
@keyNavigationList<Pagination>()
@scopedRegistry()
export class Pagination extends FormControlMixin<typeof LitElement, number>(LitElement) {
  /**
   * The number of items per page.
   */
  @property({ type: Number }) step = 10;
  /**
   * Page-size options available in the selector.
   */
  @property({ type: Array, attribute: 'step-sizes' }) stepSizes: number[] = [10, 20, 50, 100];

  /**
   * The total number of items.
   */
  @property({ type: Number }) items: number;

  /**
   * Whether the pagination is skippable to start/end.
   */
  @property({ type: Boolean }) skippable: boolean;

  /**
   * Whether the step selector has a disabled state.
   */
  @property({ type: Boolean, attribute: 'disable-step' }) disableStep: boolean;

  /**
   * Determines the container styles of component. Flat applies when nesting within other containers. Inline applies when placing within other inline content.
   */
  @property({ type: String, reflect: true }) container?: 'flat' | 'inline';

  #i18nController: I18nController<this> = new I18nController<this>(this);

  /**
   * Enables updating internal string values for internationalization.
   */
  @property({ type: Object }) i18n = this.#i18nController.i18n;

  static styles = useStyles([styles]);

  static readonly metadata = {
    tag: 'nve-pagination',
    version: '0.0.0',
    valueSchema: {
      type: 'number' as const
    }
  };

  static elementDefinitions = {
    [IconButton.metadata.tag]: IconButton,
    [Select.metadata.tag]: Select
  };

  /** @private */
  get keynavListConfig(): KeynavListConfig {
    return {
      items: this.shadowRoot!.querySelectorAll<HTMLElement>(`${IconButton.metadata.tag}, input, select`),
      layout: 'horizontal'
    };
  }

  get #currentPage() {
    return (this.value! - 1) * this.step + this.step;
  }

  get #isLastPage() {
    return this.items / this.value! === this.step;
  }

  get #isFirstPage() {
    return this.value === 1;
  }

  get #selectLabel() {
    const start = (this.value! - 1) * this.step + 1;
    const end = Math.min(this.value! * this.step, this.items || Infinity);
    return `${formatStandardNumber(start)}-${formatStandardNumber(end)}`;
  }

  get #label() {
    return this.items
      ? html`<label><slot name="suffix-label">${this.i18n.of} ${formatStandardNumber(this.items)}</slot></label>`
      : nothing;
  }

  get #previousButton() {
    return html`<nve-icon-button part="icon-button previous-icon-button"
      @click=${() => this.#setValue(this.value! - 1)}
      .disabled=${this.disabled || this.#currentPage <= this.step}
      .ariaLabel=${this.i18n.previous}
      container="inline"
      icon-name="chevron"
      direction="left"
    ></nve-icon-button>`;
  }

  get #nextButton() {
    return html`<nve-icon-button part="icon-button next-icon-button"
      @click=${() => this.#setValue(this.value! + 1)}
      .disabled=${this.disabled || this.#currentPage >= this.items}
      .ariaLabel=${this.i18n.next}
      container="inline"
      icon-name="chevron"
      direction="right"
    ></nve-icon-button>`;
  }

  get #startButton() {
    return html`<nve-icon-button part="icon-button start-icon-button"
      @click=${() => this.#setValue(1)}
      .disabled=${this.disabled || this.#currentPage <= this.step}
      .ariaLabel=${this.i18n.start}
      container="inline"
      icon-name="arrow-stop"
      direction="left"
    ></nve-icon-button>`;
  }

  get #endButton() {
    return html`<nve-icon-button part="icon-button end-icon-button"
      @click=${() => this.#setValue(this.items / this.step)}
      .disabled=${this.disabled || (this.value! - 1) * this.step + this.step >= this.items}
      .ariaLabel=${this.i18n.end}
      container="inline"
      icon-name="arrow-stop"
      direction="right"
    ></nve-icon-button>`;
  }

  #resizeObserver: ResizeObserver;

  get #select() {
    return this.disableStep
      ? html`<label>${this.#selectLabel}&nbsp;</label>`
      : html`
          <nve-select part="select" .container=${this.container}>
            <select
              .ariaLabel=${this.i18n.currentPage}
              @change=${(e: Event) => this.#setStep(parseInt((e.target as HTMLSelectElement).value, 10))}
              value=${this.step}
              .disabled=${this.disabled || this.disableStep}
            >
            ${this.stepSizes.map(i => html`<option ?selected=${this.step === i} value=${i}>${i}</option>`)}
            </select>
            <div class="select-label">${this.#selectLabel}</div>
          </nve-select>
        `;
  }

  render() {
    return html`
      <div internal-host role="presentation">
        ${
          this.skippable && this.items
            ? html`
              ${this.#startButton} ${this.#previousButton} ${this.#select} ${this.#label} ${this.#nextButton}
              ${this.#endButton}
            `
            : html` ${this.#select} ${this.#label} ${this.#previousButton} ${this.#nextButton} `
        }
      </div>
    `;
  }

  constructor() {
    super();
    this.value = 1;
  }

  async connectedCallback() {
    super.connectedCallback();
    attachInternals(this);
    this._internals.role = 'toolbar';
    await this.updateComplete;
    this.#setupLabelWidth();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#resizeObserver?.unobserve(this);
  }

  #setupLabelWidth() {
    const label = this.shadowRoot!.querySelector('.select-label');
    const select = this.shadowRoot!.querySelector('select');
    if (label && select) {
      this.#resizeObserver = new ResizeObserver(
        entries => (select!.style.minWidth = `${entries[0]!.contentRect.width + 36}px`)
      );
      this.#resizeObserver.observe(label);
    }
  }

  #setStep(value: number) {
    /* eslint-disable-next-line */
    this.step = value; // stateful due to internalized select element
    this.dispatchEvent(new CustomEvent('step-change', { detail: this.step, bubbles: true, composed: true }));
    this.#setValue(this.value!);
  }

  #setValue(value: number) {
    if (this.value !== value) {
      this.value = value;
      this.dispatchInputEvent();
      this.dispatchChangeEvent();
    }

    if (this.#isLastPage) {
      this.dispatchEvent(new CustomEvent('last-page', { bubbles: true, composed: true }));
    }

    if (this.#isFirstPage) {
      this.dispatchEvent(new CustomEvent('first-page', { bubbles: true, composed: true }));
    }
  }
}
